// RailLimitsCard — the amount band each payout rail accepts, editable.
//
// Admin-only. This is the rail's own floor and ceiling — what the provider
// will take at all — not a merchant policy: a merchant's per-transaction
// limits (Merchant › Payout limits) can only tighten it further. Changing
// a bound here changes what every merchant on that rail can send, from
// the next payout onward.
//
// Until this card existed the only way to change these was the API; an
// operator asked where in the dashboard the ₹1,000 minimum lived and there
// was no answer.
import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  listPayoutRails,
  updatePayoutRail,
  type PayoutRail,
} from "@/api/payoutRails";
import { I } from "@/components/Icons";
import { Modal, Pill } from "@/components/UI";
import { useUI } from "@/store/ui";
import { apiErrorMessage } from "@/utils/apiError";
import { fmtCcyMinor } from "@/utils/format";
import { providerLabel } from "@/utils/providerLabel";

const QK = ["payout-rails"];

function rupeesToPaise(input: string): bigint | null {
  const t = input.trim();
  if (!/^\d{1,15}$/.test(t)) return null;
  return BigInt(t) * 100n;
}

export function RailLimitsCard() {
  const {
    data: rails = [],
    isLoading,
    error,
  } = useQuery({ queryKey: QK, queryFn: listPayoutRails });
  const [editing, setEditing] = useState<PayoutRail | null>(null);

  return (
    <div className="card" style={{ marginTop: "var(--card-gap)" }}>
      <div className="card-head">
        <h3>Rail limits</h3>
        <span className="dim" style={{ fontSize: 12 }}>
          Smallest and largest single payout each rail accepts. Applies to every
          merchant on the rail, from the next payout.
        </span>
      </div>
      <div className="card-body">
        {isLoading && <div className="dim">Loading…</div>}
        {error != null && (
          <div className="dim" style={{ color: "var(--warn)" }}>
            {apiErrorMessage(error, "Could not load rail limits.")}
          </div>
        )}
        {rails.length > 0 && (
          <table className="table">
            <thead>
              <tr>
                <th>Rail</th>
                <th>Minimum</th>
                <th>Maximum</th>
                <th>Service window</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rails.map((r) => (
                <tr key={r.provider}>
                  <td>
                    {providerLabel(r.provider)}{" "}
                    {!r.configured && (
                      <Pill tone="" dot={false}>
                        default
                      </Pill>
                    )}
                  </td>
                  <td className="mono">
                    {r.railMinMinor == null
                      ? "—"
                      : fmtCcyMinor(r.railMinMinor, "INR")}
                  </td>
                  <td className="mono">
                    {r.railMaxMinor == null
                      ? "—"
                      : fmtCcyMinor(r.railMaxMinor, "INR")}
                  </td>
                  <td className="dim" style={{ fontSize: 12 }}>
                    {r.serviceWindowOpensAt && r.serviceWindowClosesAt
                      ? `${r.serviceWindowOpensAt}–${r.serviceWindowClosesAt} ${r.serviceWindowTimezone}`
                      : "any time"}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <button
                      className="btn ghost sm"
                      type="button"
                      onClick={() => setEditing(r)}
                    >
                      <I.Edit size={11} /> Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {editing && (
        <EditRailModal rail={editing} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

function EditRailModal({
  rail,
  onClose,
}: {
  rail: PayoutRail;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const addToast = useUI((s) => s.addToast);
  const [min, setMin] = useState(
    rail.railMinMinor == null ? "" : (rail.railMinMinor / 100n).toString(),
  );
  const [max, setMax] = useState(
    rail.railMaxMinor == null ? "" : (rail.railMaxMinor / 100n).toString(),
  );

  const minPaise = min.trim() === "" ? null : rupeesToPaise(min);
  const maxPaise = max.trim() === "" ? null : rupeesToPaise(max);
  const errors: string[] = [];
  if (min.trim() !== "" && minPaise === null)
    errors.push("Minimum must be a whole-rupee amount.");
  if (max.trim() !== "" && maxPaise === null)
    errors.push("Maximum must be a whole-rupee amount.");
  if (minPaise != null && maxPaise != null && minPaise > maxPaise)
    errors.push("Minimum cannot exceed maximum.");

  const mut = useMutation({
    mutationFn: () =>
      updatePayoutRail(rail.provider, {
        railMinMinor: minPaise ?? undefined,
        railMaxMinor: maxPaise ?? undefined,
        clearRailMin: min.trim() === "" && rail.railMinMinor != null,
        clearRailMax: max.trim() === "" && rail.railMaxMinor != null,
      }),
    onSuccess: (updated) => {
      qc.setQueryData<PayoutRail[]>(QK, (old) =>
        (old ?? []).map((r) => (r.provider === updated.provider ? updated : r)),
      );
      addToast({
        title: `${providerLabel(updated.provider)} limits saved`,
        body: `${updated.railMinMinor == null ? "no minimum" : "min " + fmtCcyMinor(updated.railMinMinor, "INR")} · ${
          updated.railMaxMinor == null
            ? "no maximum"
            : "max " + fmtCcyMinor(updated.railMaxMinor, "INR")
        }. In force from the next payout.`,
      });
      onClose();
    },
    onError: (err) =>
      addToast({
        title: "Could not save",
        body: apiErrorMessage(err, ""),
        variant: "error",
      }),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (errors.length === 0) mut.mutate();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`${providerLabel(rail.provider)} — rail limits`}
    >
      <form onSubmit={submit}>
        <p className="dim" style={{ fontSize: 12, marginTop: 0 }}>
          Whole rupees. Leave a field empty for no bound on that side. Payouts
          outside the band are refused before anything is sent to the rail.
        </p>
        <div className="field">
          <label className="label" htmlFor="rail-min">
            Minimum per payout (₹)
          </label>
          <input
            id="rail-min"
            className="input"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={min}
            onChange={(e) => setMin(e.target.value)}
            placeholder="e.g. 2000"
          />
        </div>
        <div className="field">
          <label className="label" htmlFor="rail-max">
            Maximum per payout (₹)
          </label>
          <input
            id="rail-max"
            className="input"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={max}
            onChange={(e) => setMax(e.target.value)}
            placeholder="e.g. 200000"
          />
        </div>
        {errors.map((e) => (
          <p
            key={e}
            style={{
              fontSize: 12,
              color: "var(--warn, #b42318)",
              margin: "4px 0",
            }}
          >
            {e}
          </p>
        ))}
        <div
          className="row gap-8"
          style={{ justifyContent: "flex-end", marginTop: 12 }}
        >
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn accent"
            type="submit"
            disabled={errors.length > 0 || mut.isPending}
          >
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}
